import React from 'react';
import { Container, Typography, Paper, Box, Alert, Grid, Card, CardContent } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import PdfMigrationUtility from './PdfMigrationUtility';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import CloudIcon from '@mui/icons-material/Cloud';

/**
 * Admin Panel - Central location for all admin tools and utilities
 */
const AdminPanel = () => {
  const { firestoreUser } = useAuth();

  // Check if user is admin
  const isAdmin = firestoreUser?.roles?.admin;

  // Redirect non-admins
  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You must be an administrator to access this page.</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <AdminPanelSettingsIcon sx={{ fontSize: 64, color: '#d98d2a', mb: 2 }} />
        <Typography 
          variant="h3" 
          sx={{ 
            color: '#d98d2a', 
            fontWeight: 'bold',
            fontFamily: '"Georgia", serif',
            mb: 1
          }}
        >
          Admin Panel
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#aaa', fontFamily: '"Georgia", serif' }}>
          System administration and management tools
        </Typography>
      </Box>

      {/* Admin Info Banner */}
      <Alert 
        severity="info" 
        icon={<AdminPanelSettingsIcon />}
        sx={{ 
          mb: 4, 
          backgroundColor: '#1a237e', 
          color: '#e3f2fd',
          '& .MuiAlert-icon': { color: '#64b5f6' }
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          Welcome, {firestoreUser?.name || 'Administrator'}!
        </Typography>
        <Typography variant="body2">
          You have administrator privileges. Use these tools to manage system resources, 
          migrations, and configurations. All actions are logged and require proper authentication.
        </Typography>
      </Alert>

      {/* Feature Cards Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            backgroundColor: '#2c2c2c', 
            border: '1px solid #555',
            height: '100%'
          }}>
            <CardContent>
              <DescriptionIcon sx={{ fontSize: 48, color: '#d98d2a', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#d98d2a', mb: 1 }}>
                PDF Management
              </Typography>
              <Typography variant="body2" sx={{ color: '#f5f5f5' }}>
                Extract and manage text from PDF documents in Benny's Journal for enhanced searchability.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            backgroundColor: '#2c2c2c', 
            border: '1px solid #555',
            height: '100%'
          }}>
            <CardContent>
              <SearchIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#4CAF50', mb: 1 }}>
                Search System
              </Typography>
              <Typography variant="body2" sx={{ color: '#f5f5f5' }}>
                Monitor and maintain the global search functionality across all resources.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ 
            backgroundColor: '#2c2c2c', 
            border: '1px solid #555',
            height: '100%'
          }}>
            <CardContent>
              <CloudIcon sx={{ fontSize: 48, color: '#2196F3', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#2196F3', mb: 1 }}>
                Cloud Functions
              </Typography>
              <Typography variant="body2" sx={{ color: '#f5f5f5' }}>
                Manage serverless functions for automated processing and background tasks.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Admin Tools Section */}
      <Paper sx={{ 
        p: 4, 
        backgroundColor: '#1c1c1c',
        border: '2px solid #d98d2a',
        borderRadius: 2
      }}>
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              color: '#d98d2a', 
              fontWeight: 'bold',
              fontFamily: '"Georgia", serif',
              mb: 1
            }}
          >
            System Management Tools
          </Typography>
          <Typography variant="body1" sx={{ color: '#aaa' }}>
            Perform administrative tasks and maintenance operations
          </Typography>
        </Box>

        {/* PDF Migration Utility - Full Size */}
        <Box sx={{ mb: 4 }}>
          <PdfMigrationUtility />
        </Box>

        {/* Future Admin Tools Section */}
        <Box sx={{ 
          p: 3, 
          backgroundColor: '#2c2c2c', 
          border: '1px solid #555',
          borderRadius: 2,
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ color: '#aaa', mb: 1 }}>
            More Tools Coming Soon
          </Typography>
          <Typography variant="body2" sx={{ color: '#777' }}>
            Additional admin utilities and management features will be added here.
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: '#555' }}>• User Management</Typography>
            <Typography variant="caption" sx={{ color: '#555' }}>• System Analytics</Typography>
            <Typography variant="caption" sx={{ color: '#555' }}>• Backup & Restore</Typography>
            <Typography variant="caption" sx={{ color: '#555' }}>• Logs & Monitoring</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Help Section */}
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ backgroundColor: '#3e2723', color: '#ffccbc' }}>
          <Typography variant="body2" fontWeight="bold">
            ⚠️ Important Notes
          </Typography>
          <Typography variant="body2">
            • All admin actions are permanent and cannot be undone
            <br />
            • Cloud Functions must be deployed for PDF extraction to work
            <br />
            • Some operations may take several minutes to complete
            <br />
            • Always verify changes in the Firebase Console after operations
          </Typography>
        </Alert>
      </Box>
    </Container>
  );
};

export default AdminPanel;
